import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, Cpu, Globe, Film, Zap, Clock, ExternalLink, Lock, Trophy } from 'lucide-react'
import { fetchNews } from '../api'

import HeroCard from './news/HeroCard'
import StoryCard from './news/StoryCard'
import TimeGroup from './news/TimeGroup'
import HeatBadge from './news/HeatBadge'
import CoverageBadge from './news/CoverageBadge'
import ExpandableSummary from './news/ExpandableSummary'
import ScoringExplainer from './news/ScoringExplainer'
import MarketTicker from './news/MarketTicker'
import WeatherTicker from './news/WeatherTicker'

const categoryConfig = {
  markets: { label: 'MARKETS', color: 'text-cyan-400' },
  tech: { label: 'TECH', color: 'text-violet-400' },
  world: { label: 'WORLD', color: 'text-rose-400' },
  sports: { label: 'SPORTS', color: 'text-emerald-400' },
  popculture: { label: 'CULTURE', color: 'text-amber-400' },
  general: { label: 'NEWS', color: 'text-gray-400' }
}

const categories = [
  { id: 'top', label: 'TOP', icon: Zap, mobileLabel: 'Top' },
  { id: 'markets', label: 'MARKETS', icon: TrendingUp, mobileLabel: 'Markets' },
  { id: 'tech', label: 'TECH', icon: Cpu, mobileLabel: 'Tech' },
  { id: 'world', label: 'WORLD', icon: Globe, mobileLabel: 'World' },
  { id: 'sports', label: 'SPORTS', icon: Trophy, mobileLabel: 'Sports' },
  { id: 'pop', label: 'CULTURE', icon: Film, mobileLabel: 'Culture' }
]

const EMPTY_DATA = {
  generated_at: new Date().toISOString(),
  next_update: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(),
  max_heat: 1,
  front_page: [],
  top_stories: [],
  categories: {},
  by_category: {},
  breaking_stories: [],
  audit_summary: null
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return ''
  const age = (Date.now() - new Date(dateStr).getTime()) / 60000
  if (age < 15) return 'Just now'
  if (age < 60) return `${Math.floor(age)}m ago`
  if (age < 1440) return `${Math.floor(age / 60)}h ago`
  return 'Yesterday'
}

function formatTime(date) {
  return new Date(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).toLowerCase()
}

function formatDate(date) {
  const d = new Date(date)
  // Compact on mobile via CSS, but provide both formats
  return {
    full: d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
    short: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
}

function groupByTimeBucket(stories) {
  const groups = new Map()
  const order = []

  for (const story of stories) {
    const bucket = story.time_bucket || formatRelativeTime(story.first_seen) || 'Recent'
    if (!groups.has(bucket)) {
      groups.set(bucket, [])
      order.push(bucket)
    }
    groups.get(bucket).push(story)
  }

  return order.map(label => ({
    label,
    stories: groups.get(label)
  }))
}

// Compact story for category sub-tabs
function CompactStory({ story, maxHeat }) {
  const cat = categoryConfig[story.category] || categoryConfig.general
  const headline = story.headline || story.title

  return (
    <article className="py-3.5 md:py-3 border-b border-white/5 last:border-0 active:bg-white/[0.03] md:hover:bg-white/[0.02] transition-colors px-1 -mx-1 rounded">
      <div className="space-y-2 md:space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] md:text-[9px] font-bold tracking-[0.15em] ${cat.color}`}>
            {cat.label}
          </span>
          {story.first_seen && (
            <span className="text-[10px] md:text-[9px] text-gray-600 flex items-center gap-0.5">
              <Clock className="w-2.5 h-2.5" />
              {formatRelativeTime(story.first_seen)}
            </span>
          )}
          <CoverageBadge score={story.coverage_score} matches={story.reference_matches} size="sm" />
        </div>
        <h4 className="text-[15px] md:text-sm font-semibold text-white leading-snug">
          {headline}
        </h4>
        {story.summary && (
          <p className="text-[13px] md:text-xs text-gray-500 leading-relaxed line-clamp-2">
            {story.summary}
          </p>
        )}
        <ExpandableSummary bullets={story.bullet_points} />
        <div className="flex items-center justify-between gap-3 text-[10px] text-gray-600 pt-1">
          <div className="flex items-center gap-2 flex-wrap">
            {story.free_links?.slice(0, 3).map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-0.5 text-[10px] md:text-[9px] px-2 py-1 md:px-1.5 md:py-0.5 rounded font-medium uppercase tracking-wider transition-colors touch-target ${
                  link.is_paywalled ? 'text-gray-600 active:text-gray-400' : 'text-gray-500 active:text-emerald-400'
                }`}
              >
                {link.is_paywalled && <Lock className="w-2.5 h-2.5 md:w-2 md:h-2" />}
                {link.name}
                <ExternalLink className="w-2.5 h-2.5 md:w-2 md:h-2 opacity-40" />
              </a>
            ))}
          </div>
          <HeatBadge score={story.heat_score} maxScore={maxHeat} size="sm" />
        </div>
      </div>
    </article>
  )
}

function TopStoriesView({ data }) {
  const stories = data?.front_page || data?.top_stories || []
  if (stories.length === 0) {
    return <p className="text-gray-600 text-sm text-center py-12">No stories available</p>
  }

  const hero = stories[0]
  const rest = stories.slice(1)
  const maxHeat = data?.max_heat || 1
  const timeGroups = groupByTimeBucket(rest)

  return (
    <div className="space-y-4 md:space-y-8 animate-fade-in">
      <HeroCard story={hero} maxHeat={maxHeat} />

      {timeGroups.length > 0 ? (
        timeGroups.map(group => (
          <TimeGroup
            key={group.label}
            label={group.label}
            stories={group.stories}
            maxHeat={maxHeat}
          />
        ))
      ) : (
        rest.map(story => (
          <StoryCard key={story.id} story={story} maxHeat={maxHeat} />
        ))
      )}
    </div>
  )
}

function CategoryView({ activeCategory, data }) {
  const catKey = activeCategory === 'pop' ? 'popculture' : activeCategory
  const catData = data?.categories?.[catKey]
  const stories = catData?.stories || data?.by_category?.[catKey] || []
  const maxHeat = data?.max_heat || 1

  return (
    <div className="bg-gray-900/50 rounded-none md:rounded-xl p-3 md:p-6 -mx-3 md:mx-0 border-y md:border border-white/5 animate-fade-in">
      <h3 className="text-[11px] md:text-xs font-bold tracking-[0.2em] text-gray-500 mb-3 md:mb-4 uppercase">
        {categories.find(c => c.id === activeCategory)?.label} Stories
      </h3>
      {stories.map(story => (
        <CompactStory key={story.id} story={story} maxHeat={maxHeat} />
      ))}
      {stories.length === 0 && (
        <p className="text-gray-600 text-sm text-center py-8">No stories in this category</p>
      )}
    </div>
  )
}

// Bottom tab bar for mobile
function MobileTabBar({ activeCategory, onSelect }) {
  return (
    <div className="bottom-tab-bar md:hidden">
      <div className="flex items-stretch justify-around px-2 pt-1.5 pb-1">
        {categories.map((cat) => {
          const Icon = cat.icon
          const isActive = activeCategory === cat.id
          return (
            <button
              key={cat.id}
              onClick={() => onSelect(cat.id)}
              className={`
                flex flex-col items-center justify-center gap-0.5 py-1.5 px-3 rounded-lg
                transition-all duration-200 touch-target
                ${isActive
                  ? 'text-white'
                  : 'text-gray-600 active:text-gray-400'
                }
              `}
            >
              <div className="relative">
                <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-400' : ''}`} />
                {isActive && (
                  <div className="absolute -inset-1 bg-emerald-400 rounded-full blur-md opacity-20" />
                )}
              </div>
              <span className={`text-[10px] font-semibold tracking-wide ${isActive ? 'text-emerald-400' : ''}`}>
                {cat.mobileLabel}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default function NewsTab({ autoRefresh }) {
  const [activeCategory, setActiveCategory] = useState('top')

  const { data, isLoading } = useQuery({
    queryKey: ['news'],
    queryFn: fetchNews,
    refetchInterval: autoRefresh ? 5 * 60 * 1000 : false,
  })

  const newsData = data || EMPTY_DATA
  const dateInfo = formatDate(newsData.generated_at)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="w-10 h-10 md:w-8 md:h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm tracking-wider">LOADING</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="max-w-4xl mx-auto">
        {/* Market Ticker - Top 5 by volume (static) */}
        <MarketTicker autoRefresh={autoRefresh} />

        {/* Weather Ticker - Current + 3-day forecast */}
        <WeatherTicker autoRefresh={autoRefresh} />

        {/* Masthead - compact on mobile */}
        <header className="text-center py-3 md:py-8 mb-3 md:mb-8 border-b border-white/10">
          <h1 className="text-2xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-0.5 md:mb-1">
            THE BRO TIMES
          </h1>
          <p className="text-[9px] md:text-[10px] tracking-[0.4em] uppercase mb-2 md:mb-6 text-emerald-400/80 font-bold italic">
            by JR
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-6 text-[10px] md:text-xs text-gray-500">
            {/* Short date on mobile, full on desktop */}
            <span className="md:hidden">{dateInfo.short}</span>
            <span className="hidden md:inline">{dateInfo.full}</span>
            <span className="w-1 h-1 bg-gray-700 rounded-full" />
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(newsData.generated_at)}
            </span>
            {newsData.audit_summary?.reference_feeds_used?.length > 0 && (
              <>
                <span className="hidden md:block w-1 h-1 bg-gray-700 rounded-full" />
                <span className="hidden md:inline text-emerald-400/70 flex items-center gap-1">
                  <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Cross-checked: {newsData.audit_summary.reference_feeds_used.join(', ')}
                </span>
              </>
            )}
          </div>

          <div className="flex justify-center mt-3 md:mt-4">
            <ScoringExplainer />
          </div>
        </header>

        {/* Desktop category nav - hidden on mobile (replaced by bottom tab bar) */}
        <nav className="hidden md:flex justify-center gap-1 mb-10">
          {categories.map((cat) => {
            const Icon = cat.icon
            const isActive = activeCategory === cat.id
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`
                  flex items-center gap-2 px-4 py-2 text-xs font-bold tracking-[0.15em]
                  transition-all rounded-full whitespace-nowrap
                  ${isActive
                    ? 'bg-white text-black'
                    : 'text-gray-500 hover:text-white hover:bg-white/5'
                  }
                `}
              >
                <Icon className="w-3.5 h-3.5" />
                {cat.label}
              </button>
            )
          })}
        </nav>

        {/* Content */}
        <div className="space-y-4 md:space-y-8">
          {activeCategory === 'top' ? (
            <TopStoriesView data={newsData} />
          ) : (
            <CategoryView activeCategory={activeCategory} data={newsData} />
          )}
        </div>

        {/* Footer - only desktop */}
        <footer className="hidden md:block mt-16 pt-8 border-t border-white/5 text-center">
          <p className="text-[10px] tracking-[0.2em] text-gray-600 uppercase">
            Heat-ranked news aggregation
          </p>
        </footer>
      </div>

      {/* Mobile bottom tab bar */}
      <MobileTabBar activeCategory={activeCategory} onSelect={setActiveCategory} />
    </>
  )
}
